import { observer } from 'mobx-react';
import React, { useMemo, useState } from 'react';
import classnames from 'classnames';
import { UiIcon } from 'shared/uikit';
import { ICONS } from 'shared/constants';
import { useDashboardDict } from './../../../../../../context/useDashboardDict';
import './styles.scss';
import { formatValueWithUnits } from './../../../../../../utilities/formatValueWithUnits';

type PropsType = {
    labels: Record<string, string>;
    items: any[];
    themeHead?: 'dark' | 'grey'
};

type SortField = 'product_group' | 'plan' | 'fact' | 'status' | null;
type SortDirection = 'asc' | 'desc' | null;

export const PPDWidgetProductGroupsPricesTable = observer(({ labels, items = [], themeHead = 'dark' }: PropsType) => {
    const { assortment, units } = useDashboardDict();

    const [sortField, setSortField] = useState<SortField>('product_group');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
    const [isDirectionAnimated, setIsDirectionAnimated] = useState(false);

    const handleSort = (field: Exclude<SortField, null>) => {
        if (sortField === field) {
            setIsDirectionAnimated(true);
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setIsDirectionAnimated(false);
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const sortedItems = useMemo(() => {
        const statusOrderDesc = ['ahead', 'normal', 'behind'];
        const statusOrderAsc = [...statusOrderDesc].reverse();
        const activeSortField = sortField || 'product_group';
        const activeSortDirection = sortDirection || 'asc';
        const assortmentById = new Map(assortment?.map(group => [group.id, group]));

        return [...items].sort((a, b) => {
            const groupA = assortmentById.get(a.assortment_id);
            const groupB = assortmentById.get(b.assortment_id);

            let valA: string | number = '';
            let valB: string | number = '';

            switch (activeSortField) {
                case 'product_group':
                    valA = (groupA?.name || '').toLowerCase();
                    valB = (groupB?.name || '').toLowerCase();
                    break;
                case 'plan':
                    valA = a.plan?.amount || 0;
                    valB = b.plan?.amount || 0;
                    break;
                case 'fact':
                    valA = a.fact?.amount || 0;
                    valB = b.fact?.amount || 0;
                    break;
                case 'status': {
                    const order = activeSortDirection === 'asc' ? statusOrderAsc : statusOrderDesc;
                    const indexA = order.indexOf(a.status);
                    const indexB = order.indexOf(b.status);
                    return (indexA === -1 ? order.length : indexA) - (indexB === -1 ? order.length : indexB);
                }
            }

            if (typeof valA === 'string' && typeof valB === 'string') {
                const cmp = valA.localeCompare(valB, 'ru');
                return activeSortDirection === 'asc' ? cmp : -cmp;
            }

            if (typeof valA === 'number' && typeof valB === 'number') {
                return activeSortDirection === 'asc' ? valA - valB : valB - valA;
            }

            return 0;
        });
    }, [items, assortment, sortField, sortDirection]);

    const renderHeader = (label: string, key?: Exclude<SortField, null>) => {
        const isActive = key && sortField === key;
        return (
            <th
                className={classnames(
                    "ppd-widget-product-groups-prices-table__header-cell",
                    { "ppd-widget-product-groups-prices-table__header-cell--sorted": isActive }
                )}
                onClick={() => key && handleSort(key)}
            >
                <span className="ppd-widget-product-groups-prices-table__header-text">
                    {label}
                    {key && (
                        <span
                            className={classnames("ppd-widget-product-groups-prices-table__sort-arrow", {
                                "ppd-widget-product-groups-prices-table__sort-arrow--active": isActive,
                                "ppd-widget-product-groups-prices-table__sort-arrow--animated": isActive && isDirectionAnimated,
                                "ppd-widget-product-groups-prices-table__sort-arrow--asc": isActive && sortDirection === "asc",
                                "ppd-widget-product-groups-prices-table__sort-arrow--desc": isActive && sortDirection === "desc",
                            })}
                        >
                            <UiIcon
                                icon={ICONS.ARROW_UP_DOWN}
                                size={12}
                                color={themeHead === 'grey' ? "#787B83" : "#15171C"}
                                className="ppd-widget-product-groups-prices-table__sort-arrow-icon ppd-widget-product-groups-prices-table__sort-arrow-icon--default"
                            />
                            <UiIcon
                                icon={ICONS.CHEVRON_DOWN}
                                size={12}
                                color="#15171C"
                                className="ppd-widget-product-groups-prices-table__sort-arrow-icon ppd-widget-product-groups-prices-table__sort-arrow-icon--active"
                            />
                        </span>
                    )}
                </span>
            </th>
        );
    };

    return (
        <table className="ppd-widget-product-groups-prices-table">
            <thead
                className={classnames("ppd-widget-product-groups-prices-table__thead", {
                    "ppd-widget-product-groups-prices-table__thead--grey": themeHead === 'grey',
                })}
            >
                <tr className="ppd-widget-product-groups-prices-table__header-row">
                    {renderHeader(labels.product_group, 'product_group')}
                    {renderHeader(labels.plan, 'plan')}
                    {renderHeader(labels.fact, 'fact')}
                    {renderHeader(labels.status, 'status')}
                </tr>
            </thead>

            <tbody className="ppd-widget-product-groups-prices-table__tbody">
                {sortedItems.map((item, index) => {
                    const group = assortment?.find(a => a.id === item.assortment_id);
                    if (!group) return null;

                    return (
                        <tr key={`${item.assortment_id}-${index}`} className="ppd-widget-product-groups-prices-table__row">
                            <td className="ppd-widget-product-groups-prices-table__cell ppd-widget-product-groups-prices-table__cell--product">
                                {
                                    group?.image && (
                                        <img
                                            className="ppd-widget-product-groups-prices-table__product-image"
                                            src={group.image}
                                        />
                                    )
                                }
                                <span className="ppd-widget-product-groups-prices-table__product-name">{group.name || "-"}</span>
                            </td>

                            <td className="ppd-widget-product-groups-prices-table__cell">
                                <span className={"ppd-widget-product-groups-prices-table__cell-value"}>
                                    {formatValueWithUnits(item.plan, units) ? formatValueWithUnits(item.plan, units) : "-"}
                                </span>
                            </td>

                            <td className="ppd-widget-product-groups-prices-table__cell">
                                <span className={`ppd-widget-product-groups-prices-table__cell-value ppd-widget-product-groups-prices-table__cell-value--${item.status}`}>
                                    {formatValueWithUnits(item.fact, units) ? formatValueWithUnits(item.fact, units) : "-"}
                                </span>
                            </td>
                            <td className="ppd-widget-product-groups-prices-table__cell ppd-widget-product-groups-prices-table__cell--status">
                                <span
                                    className={classnames(
                                        "ppd-widget-product-groups-prices-table__badge",
                                        `ppd-widget-product-groups-prices-table__badge--${item.status}`
                                    )}
                                >
                                    {labels[item.status as keyof typeof labels]}
                                </span>
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
});
